import React from "react";
import TransactionList from "../Portfolio/TransactionList";
import { DEMO_HOLDINGS, DEMO_TRANSACTIONS } from "./demoData";
import DemoCTA from "./DemoCTA";
import { formatCurrency, formatAccountSubtype } from "../../utils/formatters";

function isValidTicker(ticker) {
    return typeof ticker === "string" && /^[A-Z0-9]{1,5}$/.test(ticker);
}

export default function DemoAccountDetail({ account, onBack }) {
    const displaySubtype = formatAccountSubtype(account.subtype);

    // Determine if this is an investment account
    const isInvestment = account.type?.toUpperCase() === "INVESTMENT" ||
                         account.subtype === "ira" ||
                         account.subtype === "roth" ||
                         account.subtype === "_401k" ||
                         account.subtype === "401k" ||
                         account.subtype === "brokerage";

    // Get holdings for this account
    const accountHoldings = DEMO_HOLDINGS.filter(
        h => h.accountId === account.accountId
    ).filter(h => h.name !== "U S Dollar");

    // Get transactions for this account
    const accountTransactions = DEMO_TRANSACTIONS[account.accountId] || [];

    return (
        <div>
            <button
                onClick={onBack}
                className="flex items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors mb-6"
            >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Portfolio
            </button>

            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">{account.name}</h1>
                <p className="text-gray-500 mt-1">{displaySubtype}</p>
            </div>

            {/* Bank Accounts: Transaction List */}
            {!isInvestment && (
                <div className="card p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Transactions</h2>
                    {accountTransactions.length > 0 ? (
                        <TransactionList transactions={accountTransactions} accountType={account.type} />
                    ) : (
                        <p className="text-gray-500">No transactions available for this account.</p>
                    )}
                </div>
            )}

            {/* Investment Accounts: Holdings Table */}
            {isInvestment && (
                <div className="card p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Holdings</h2>

                    {accountHoldings.length === 0 ? (
                        <p className="text-gray-500">No holdings found for this account.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="text-left text-sm font-medium text-gray-500 pb-3">Ticker</th>
                                        <th className="text-left text-sm font-medium text-gray-500 pb-3">Name</th>
                                        <th className="text-right text-sm font-medium text-gray-500 pb-3">Quantity</th>
                                        <th className="text-right text-sm font-medium text-gray-500 pb-3">Cost/Share</th>
                                        <th className="text-right text-sm font-medium text-gray-500 pb-3">Cost Basis</th>
                                        <th className="text-right text-sm font-medium text-gray-500 pb-3">Price</th>
                                        <th className="text-right text-sm font-medium text-gray-500 pb-3">Gain/Loss</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accountHoldings.map((h) => {
                                        const displayTicker = isValidTicker(h.tickerSymbol)
                                            ? h.tickerSymbol
                                            : h.name;

                                        return (
                                            <tr key={h.securityId} className="border-b border-gray-200">
                                                <td className="py-3 text-gray-900 font-medium">
                                                    {displayTicker || h.securityId}
                                                </td>
                                                <td className="py-3 text-gray-500">
                                                    {h.name || "<unknown>"}
                                                </td>
                                                <td className="py-3 text-right text-gray-900">
                                                    {h.quantity}
                                                </td>
                                                <td className="py-3 text-right text-gray-900">
                                                    {formatCurrency(h.costShare)}
                                                </td>
                                                <td className="py-3 text-right text-gray-900">
                                                    {formatCurrency(h.costBasis)}
                                                </td>
                                                <td className="py-3 text-right text-gray-900">
                                                    {formatCurrency(h.closePrice)}
                                                </td>
                                                <td className={`py-3 text-right font-medium ${h.gainLoss >= 0 ? "text-accent-600" : "text-danger-500"}`}>
                                                    {formatCurrency(h.gainLoss)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <DemoCTA variant="inline" />
        </div>
    );
}
